/**
 * ScansioneInventario — modalità scansione barcode/QR per la gestione
 * inventario. Compatibile con qualsiasi lettore HID (USB o Bluetooth) che
 * si comporta come una tastiera: quando l'operatore scansiona un codice,
 * il lettore digita il valore letto e invia Enter.
 *
 * Modalità:
 *   - CARICO:      registra un movimento di carico per articolo
 *   - SCARICO:     registra un movimento di scarico
 *   - CONTEGGIO:   accumula quantità per articolo e registra una RETTIFICA
 *                  a fine sessione (impostando la giacenza al valore contato)
 *
 * Design UX:
 *   - Input barcode nascosto ma sempre a focus (autofocus + re-focus on blur)
 *   - Anti-doppia lettura: debounce 400 ms sullo stesso codice
 *   - Feedback sonoro (WebAudio, senza asset) + toast su ogni scan
 *   - Se codice non trovato → modal creazione rapida articolo (nome +
 *     unità + quantità iniziale + categoria); il barcode viene associato.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ScanLine, Plus, Minus, ClipboardList, X, Trash2, CheckCircle2,
  AlertTriangle, Package, RefreshCw,
} from "lucide-react";
import api from "@/api";

const _now = () => new Date().toISOString();
const _fmtTime = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Beep sintetico (Web Audio) — nessuna dipendenza esterna.
// Il primo beep richiede un gesto utente per policy autoplay dei browser;
// dato che la modalità scansione parte da click "Avvia scansione", è OK.
let _audioCtx = null;
function beep(freq = 880, ms = 90, gain = 0.15) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = _audioCtx.createOscillator();
    const g = _audioCtx.createGain();
    osc.frequency.value = freq;
    osc.type = "sine";
    g.gain.value = gain;
    osc.connect(g).connect(_audioCtx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); }, ms);
  } catch { /* audio non disponibile → silenzioso */ }
}
const beepOk = () => beep(1200, 70);
const beepDup = () => { beep(600, 60); setTimeout(() => beep(600, 60), 100); };
const beepErr = () => { beep(220, 200); };

export default function ScansioneInventario({
  onClose,
  cantieri = [],
  richiestaIniziale = null,
}) {
  const qc = useQueryClient();
  const richiestaBloccata = Boolean(richiestaIniziale?.richiesta_id);

  const [mode, setMode] = useState(
    richiestaBloccata ? "scarico" : "carico"
  );        // carico | scarico | conteggio
  const [cantiereId, setCantiereId] = useState(
    richiestaIniziale?.cantiere_id || ""
  );
  const [richiestaId, setRichiestaId] = useState(
    richiestaIniziale?.richiesta_id || ""
  );
  const [scans, setScans] = useState([]);            // [{materiale_id, nome, barcode, unita, quantita, giacenza, ...}]
  const [barcodeBuf, setBarcodeBuf] = useState("");
  const [lastScan, setLastScan] = useState(null);    // { code, ts } per debounce
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(null);
  const [richiestaLive, setRichiestaLive] = useState(richiestaIniziale);    // barcode non trovato → prompt creazione rapida
  const inputRef = useRef(null);

  // Mantieni il focus sul lettore SOLO quando non è aperto il modal
  // "Barcode non trovato". Nel modal l'utente deve poter cercare e cliccare
  // normalmente senza che l'input invisibile si riprenda il focus.
  useEffect(() => {
    if (notFound) return;

    const focus = () => inputRef.current && inputRef.current.focus();
    focus();

    const onBlur = () => setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);

    const onClick = (e) => {
      if (e.target.closest("[data-scan-safe]")) return;
      focus();
    };

    const input = inputRef.current;
    input && input.addEventListener("blur", onBlur);
    document.addEventListener("click", onClick);

    return () => {
      input && input.removeEventListener("blur", onBlur);
      document.removeEventListener("click", onClick);
    };
  }, [notFound]);

  // Reset focus quando notFound modal chiuso
  useEffect(() => {
    if (!notFound && inputRef.current) inputRef.current.focus();
  }, [notFound]);

  const handleScan = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code || saving) return;

    if (
      lastScan &&
      lastScan.code === code &&
      Date.now() - lastScan.ts < 400
    ) {
      setBarcodeBuf("");
      beepDup();
      return;
    }

    setLastScan({ code, ts: Date.now() });
    setBarcodeBuf("");

    try {
      const { data: mat } = await api.get(
        `/magazzino/materiali/by-barcode/${encodeURIComponent(code)}`
      );

      if (richiestaBloccata) {
        setSaving(true);

        try {
          const { data } = await api.post(
            "/magazzino/uscita-cantiere",
            {
              richiesta_id: richiestaId,
              righe: [
                {
                  materiale_id: mat.materiale_id,
                  quantita: 1,
                  barcode: code,
                },
              ],
            }
          );

          const { data: elenco } = await api.get("/richieste-materiali");
          const aggiornata = (elenco || []).find(
            (r) => r.richiesta_id === richiestaId
          );

          if (aggiornata) setRichiestaLive(aggiornata);

          setScans((prev) => {
            const existing = prev.find(
              (r) => r.materiale_id === mat.materiale_id
            );

            if (existing) {
              return prev.map((r) =>
                r.materiale_id === mat.materiale_id
                  ? {
                      ...r,
                      quantita: r.quantita + 1,
                      giacenza: Math.max(0, Number(r.giacenza || 0) - 1),
                      last_scan_at: _now(),
                    }
                  : r
              );
            }

            return [
              {
                materiale_id: mat.materiale_id,
                nome: mat.nome,
                codice: mat.codice || "",
                barcode: mat.barcode || code,
                unita_misura: mat.unita_misura || "pz",
                categoria: mat.categoria,
                giacenza: Math.max(
                  0,
                  Number(mat.quantita_disponibile || 0) - 1
                ),
                quantita: 1,
                foto: mat.foto_base64 || null,
                first_scan_at: _now(),
                last_scan_at: _now(),
              },
              ...prev,
            ];
          });

          qc.invalidateQueries({ queryKey: ["magazzino"] });
          qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
          qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
          qc.invalidateQueries({ queryKey: ["bolle"] });

          beepOk();

          toast.success(
            `${mat.nome} · -1 dal magazzino · ${data.stato_richiesta}`,
            { duration: 1200 }
          );
        } catch (e) {
          beepErr();
          toast.error(
            e?.response?.data?.detail ||
            "Materiale non valido per questa richiesta"
          );
        } finally {
          setSaving(false);
          setTimeout(() => inputRef.current?.focus(), 50);
        }

        return;
      }

      setScans((prev) => {
        const existing = prev.find(
          (r) => r.materiale_id === mat.materiale_id
        );

        if (existing) {
          return prev.map((r) =>
            r.materiale_id === mat.materiale_id
              ? {
                  ...r,
                  quantita: r.quantita + 1,
                  last_scan_at: _now(),
                }
              : r
          );
        }

        return [
          {
            materiale_id: mat.materiale_id,
            nome: mat.nome,
            codice: mat.codice || "",
            barcode: mat.barcode || code,
            unita_misura: mat.unita_misura || "pz",
            categoria: mat.categoria,
            giacenza: Number(mat.quantita_disponibile || 0),
            quantita: 1,
            foto: mat.foto_base64 || null,
            first_scan_at: _now(),
            last_scan_at: _now(),
          },
          ...prev,
        ];
      });

      beepOk();
      toast.success(`${mat.nome} — +1 ${mat.unita_misura || "pz"}`, {
        duration: 900,
      });

    } catch (e) {
      if (e?.response?.status === 404) {
        beepErr();

        if (richiestaBloccata) {
          toast.error(`Barcode non riconosciuto: ${code}`);
        } else {
          setNotFound(code);
        }
      } else {
        beepErr();
        toast.error(
          `Errore lookup: ${e?.response?.data?.detail || e.message}`
        );
      }
    }
  };

  const updateQty = (materiale_id, newQty) => {
    if (newQty < 0) return;
    setScans((prev) => prev.map((r) => r.materiale_id === materiale_id ? { ...r, quantita: newQty } : r));
  };
  const removeRow = (materiale_id) => setScans((prev) => prev.filter((r) => r.materiale_id !== materiale_id));


  const { data: richiesteScarico = [] } = useQuery({
    queryKey: ["richieste-materiali-scarico"],
    queryFn: () =>
      api.get("/richieste-materiali").then((r) =>
        (r.data || []).filter((x) =>
          !["annullata", "rifiutata", "ritirata"].includes(x.stato)
        )
      ),
    enabled: mode === "scarico",
  });

  const richiestaSelezionata =
    richiesteScarico.find((r) => r.richiesta_id === richiestaId) || null;

  useEffect(() => {
    if (richiestaBloccata) {
      setMode("scarico");
      setRichiestaId(richiestaIniziale.richiesta_id);
      setCantiereId(richiestaIniziale.cantiere_id || "");
      setRichiestaLive(richiestaIniziale);
      return;
    }

    if (mode !== "scarico") {
      setRichiestaId("");
      return;
    }

    if (richiestaSelezionata?.cantiere_id) {
      setCantiereId(richiestaSelezionata.cantiere_id);
    }
  }, [
    mode,
    richiestaId,
    richiestaSelezionata?.cantiere_id,
    richiestaBloccata,
    richiestaIniziale,
  ]);

  const saveAll = async () => {
    if (scans.length === 0) return;

    // USCITA VERSO CANTIERE:
    // una sola chiamata -> movimenti + una sola bolla + aggiornamento richiesta
    if (mode === "scarico") {
      if (!richiestaId) {
        toast.error("Seleziona prima la Richiesta Materiali");
        return;
      }

      setSaving(true);

      try {
        const payload = {
          richiesta_id: richiestaId,
          righe: scans
            .filter((r) => Number(r.quantita) > 0)
            .map((r) => ({
              materiale_id: r.materiale_id,
              quantita: Number(r.quantita),
              barcode: r.barcode || "",
            })),
        };

        const { data } = await api.post(
          "/magazzino/uscita-cantiere",
          payload
        );

        toast.success(
          `${data.numero_bolla} creata · richiesta ${data.numero_richiesta} ${data.stato_richiesta}`
        );

        qc.invalidateQueries({ queryKey: ["magazzino"] });
        qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
        qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
        qc.invalidateQueries({ queryKey: ["richieste-materiali-scarico"] });
        qc.invalidateQueries({ queryKey: ["bolle"] });

        setScans([]);
        setRichiestaId("");
        setCantiereId("");
      } catch (e) {
        toast.error(
          e?.response?.data?.detail ||
          "Errore durante l'uscita materiale verso il cantiere"
        );
      } finally {
        setSaving(false);
      }

      return;
    }

    // CARICO / CONTEGGIO: comportamento precedente
    setSaving(true);
    let ok = 0, ko = 0;

    for (const r of scans) {
      if (r.quantita <= 0) continue;

      try {
        const payload = {
          materiale_id: r.materiale_id,
          cantiere_id: cantiereId || null,
          quantita: Number(r.quantita),
          metodo: mode === "conteggio" ? "conteggio" : "scan_barcode",
          note: mode === "conteggio"
            ? `Conteggio inventario · barcode ${r.barcode}`
            : `Scansione barcode ${r.barcode}`,
          tipo: mode === "carico" ? "carico" : "rettifica",
        };

        await api.post("/magazzino/movimenti", payload);
        ok++;
      } catch (e) {
        ko++;
        toast.error(
          `${r.nome}: ${e?.response?.data?.detail || "errore"}`
        );
      }
    }

    setSaving(false);

    if (ok > 0) {
      toast.success(
        `Salvate ${ok} operazioni${ko ? ` · ${ko} errori` : ""}`
      );
    }

    qc.invalidateQueries({ queryKey: ["magazzino"] });
    qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });

    if (ko === 0) setScans([]);
  };

  const totalRighe = scans.length;
  const totalPezzi = useMemo(() => scans.reduce((s, r) => s + Number(r.quantita || 0), 0), [scans]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm flex flex-col"
      data-testid="scansione-inventario"
    >
      {/* Barra superiore */}
      <div className="bg-slate-900 text-white p-4 flex items-center gap-3 border-b border-slate-700" data-scan-safe>
        <ScanLine className="w-7 h-7 text-emerald-400" />
        <div className="flex-1">
          <div className="overline text-emerald-400">Modalità scansione</div>
          <h2 className="font-display font-black text-xl md:text-2xl">Inventario · Lettore Barcode/QR</h2>
        </div>
        <button
          onClick={onClose}
          data-testid="btn-chiudi-scansione"
          className="p-2 hover:bg-slate-800 rounded-sm"
          title="Chiudi scansione"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Selettore modalità + cantiere */}
      <div className="bg-slate-800 p-3 flex flex-wrap items-center gap-2 md:gap-3 border-b border-slate-700" data-scan-safe>
        <div className="flex gap-1">
          {[
            { key: "carico", label: "CARICO", color: "bg-emerald-600 hover:bg-emerald-700" },
            { key: "scarico", label: "SCARICO", color: "bg-amber-600 hover:bg-amber-700" },
            { key: "conteggio", label: "CONTEGGIO", color: "bg-blue-600 hover:bg-blue-700" },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`mode-${m.key}`}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-bold uppercase tracking-wider text-white rounded-sm ${
                mode === m.key ? m.color : "bg-slate-700 hover:bg-slate-600"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <select
          value={cantiereId}
          onChange={(e) => setCantiereId(e.target.value)}
          disabled={mode === "scarico" && !!richiestaId}
          data-testid="scan-cantiere-select"
          className="px-3 py-2 bg-slate-700 text-white text-xs md:text-sm border border-slate-600 rounded-sm"
        >
          <option value="">— Nessun cantiere —</option>
          {cantieri.map((c) => (
            <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
          ))}
        </select>

        {mode === "scarico" && (
          <select
            value={richiestaId}
            onChange={(e) => setRichiestaId(e.target.value)}
            data-testid="scan-richiesta-select"
            className="px-3 py-2 bg-amber-700 text-white text-xs md:text-sm border border-amber-500 rounded-sm min-w-[260px]"
          >
            <option value="">— Seleziona Richiesta Materiali —</option>

            {richiesteScarico.map((r) => (
              <option
                key={r.richiesta_id}
                value={r.richiesta_id}
              >
                {r.numero_richiesta} · {r.cantiere_nome || "Senza cantiere"} · {r.fornitore_nome || ""}
              </option>
            ))}
          </select>
        )}

        <div className="ml-auto text-white text-xs md:text-sm">
          <b className="text-emerald-400 text-xl md:text-2xl font-mono">{totalRighe}</b> articoli · <b className="text-xl md:text-2xl font-mono">{totalPezzi}</b> pezzi
        </div>
      </div>

      {/* Prompt "puntare il lettore" */}
      <div className="bg-emerald-500/10 border-b border-emerald-500/30 p-4 text-center" data-scan-safe>
        <div className="text-emerald-300 text-xs md:text-sm font-bold uppercase tracking-widest mb-1 flex items-center justify-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          In attesa di scansione…
        </div>
        <div className="text-white/70 text-xs">
          Punta il lettore barcode/QR verso l'articolo — il codice verrà letto automaticamente
        </div>
        {/* Input invisibile ma sempre a focus */}
        <input
          ref={inputRef}
          value={barcodeBuf}
          onChange={(e) => setBarcodeBuf(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleScan(barcodeBuf);
            }
          }}
          data-testid="barcode-input"
          autoComplete="off"
          autoCapitalize="off"
          data-no-uppercase
          className="absolute opacity-0 pointer-events-none"
          aria-label="Campo scansione barcode"
        />
      </div>

      {richiestaBloccata && richiestaLive && (
        <div className="bg-slate-100 border-b border-slate-300 p-3 md:p-4" data-scan-safe>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                  Richiesta in prelievo
                </div>
                <div className="font-black text-lg">
                  {richiestaLive.numero_richiesta}
                </div>
                <div className="text-xs text-slate-500">
                  {richiestaLive.cantiere_nome || "Cantiere"}
                </div>
              </div>
              <div className="text-xs font-bold bg-white border border-slate-300 px-3 py-2 rounded-sm">
                {richiestaLive.stato}
              </div>
            </div>

            <div className="grid gap-2">
              {(richiestaLive.materiali || []).map((m, idx) => {
                const richiesti = Number(m.quantita || 0);
                const ritirati = Number(m.quantita_ritirata || 0);
                const residui = Math.max(0, richiesti - ritirati);
                const completo = residui <= 0;

                return (
                  <div
                    key={m.riga_id || `${m.codice_articolo}-${idx}`}
                    className={`p-3 border rounded-sm flex items-center gap-3 ${
                      completo
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-white border-slate-200"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">
                        {m.descrizione}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {m.codice_articolo || "Senza codice"}
                      </div>
                    </div>

                    <div className="text-right text-xs shrink-0">
                      <div>
                        Richiesti: <b>{richiesti}</b> {m.unita_misura || "pz"}
                      </div>
                      <div className={completo ? "text-emerald-700" : "text-amber-700"}>
                        Ritirati: <b>{ritirati}</b> · Residui: <b>{residui}</b>
                      </div>
                    </div>

                    {completo && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Lista scansioni */}
      <div className="flex-1 overflow-y-auto p-3 md:p-4">
        {scans.length === 0 ? (
          <div className="text-center text-slate-400 py-16" data-scan-safe>
            <Package className="w-16 h-16 mx-auto mb-4 opacity-40" />
            <div className="text-lg font-semibold">Nessun articolo scansionato</div>
            <div className="text-xs mt-2">Inizia scansionando un codice a barre / QR</div>
          </div>
        ) : (
          <div className="space-y-2 max-w-4xl mx-auto">
            {scans.map((r) => (
              <div
                key={r.materiale_id}
                data-testid={`scan-row-${r.materiale_id}`}
                data-scan-safe
                className="bg-white rounded-sm shadow-lg p-3 md:p-4 flex items-center gap-3"
              >
                {r.foto ? (
                  <img src={r.foto} alt="" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-sm shrink-0" />
                ) : (
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-100 rounded-sm flex items-center justify-center shrink-0">
                    <Package className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base md:text-lg truncate">{r.nome}</div>
                  <div className="text-xs text-slate-500 truncate">
                    <b>Barcode:</b> {r.barcode} · <b>Giacenza:</b> {r.giacenza} {r.unita_misura}
                    {mode === "conteggio" && (
                      <> · <b className="text-blue-700">Δ:</b> {(r.quantita - r.giacenza).toFixed(0)}</>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {_fmtTime(r.first_scan_at)}
                    {r.last_scan_at !== r.first_scan_at && ` → ${_fmtTime(r.last_scan_at)}`}
                  </div>
                </div>
                <div className="flex items-center gap-1 md:gap-2 shrink-0">
                  <button
                    onClick={() => updateQty(r.materiale_id, r.quantita - 1)}
                    disabled={r.quantita <= 0}
                    data-testid={`scan-minus-${r.materiale_id}`}
                    className="p-2 md:p-3 rounded-sm bg-rose-100 hover:bg-rose-200 text-rose-700 disabled:opacity-40"
                  >
                    <Minus className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={r.quantita}
                    onChange={(e) => updateQty(r.materiale_id, Number(e.target.value) || 0)}
                    data-testid={`scan-qty-${r.materiale_id}`}
                    className="w-16 md:w-20 text-center text-xl md:text-2xl font-black font-mono border border-slate-300 rounded-sm py-1"
                    data-no-uppercase
                  />
                  <button
                    onClick={() => updateQty(r.materiale_id, r.quantita + 1)}
                    data-testid={`scan-plus-${r.materiale_id}`}
                    className="p-2 md:p-3 rounded-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                  >
                    <Plus className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  <button
                    onClick={() => removeRow(r.materiale_id)}
                    data-testid={`scan-del-${r.materiale_id}`}
                    className="p-2 rounded-sm text-rose-600 hover:bg-rose-100 ml-1"
                    title="Rimuovi riga"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer azioni */}
      <div className="bg-slate-900 border-t border-slate-700 p-3 md:p-4 flex items-center gap-2 md:gap-3" data-scan-safe>
        <button
          onClick={() => { if (window.confirm("Cancellare tutte le scansioni non salvate?")) setScans([]); }}
          disabled={scans.length === 0 || saving}
          data-testid="btn-clear-scans"
          className="px-3 md:px-4 py-2 md:py-3 border border-slate-600 text-slate-300 hover:bg-slate-800 text-xs md:text-sm font-bold uppercase rounded-sm disabled:opacity-40"
        >
          Cancella tutto
        </button>
        <div className="flex-1" />
        <button
          onClick={saveAll}
          disabled={scans.length === 0 || saving}
          data-testid="btn-salva-scansioni"
          className="px-4 md:px-6 py-2 md:py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm md:text-base font-black uppercase tracking-wider rounded-sm shadow-lg flex items-center gap-2 disabled:opacity-40"
        >
          <CheckCircle2 className="w-5 h-5" />
          {saving
            ? "Salvataggio…"
            : mode === "scarico"
              ? `Crea bolla e scarica (${totalRighe})`
              : `Salva ${mode === "conteggio" ? "conteggio" : mode} (${totalRighe})`}
        </button>
      </div>

      {notFound && (
        <NotFoundModal
          code={notFound}
          onClose={() => setNotFound(null)}
          onCreated={(mat) => {
            setNotFound(null);
            // Aggiungi immediatamente alla lista con quantità 1
            setScans((prev) => [
              {
                materiale_id: mat.materiale_id,
                nome: mat.nome,
                codice: mat.codice || "",
                barcode: mat.barcode || notFound,
                unita_misura: mat.unita_misura || "pz",
                giacenza: Number(mat.quantita_disponibile || 0),
                quantita: 1,
                foto: mat.foto_base64 || null,
                first_scan_at: _now(),
                last_scan_at: _now(),
              },
              ...prev,
            ]);
            beepOk();
          }}
        />
      )}
    </div>
  );
}

function NotFoundModal({ code, onClose, onCreated }) {
  const [mode, setMode] = useState("choose"); // choose | crea | associa
  const [suggested, setSuggested] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    unita_misura: "pz",
    categoria: "generico",
    quantita_disponibile: 0,
    prezzo_unitario: 0,
  });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    // Pre-suggerimenti quando l'utente inizia a scrivere il nome in modalità crea
    if (mode !== "crea" || !form.nome || form.nome.length < 2) { setSuggested([]); return; }
    setSuggLoading(true);
    const t = setTimeout(() => {
      api.get(`/magazzino/materiali/simili?q=${encodeURIComponent(form.nome)}&limit=5`)
        .then((r) => setSuggested(r.data)).catch(() => setSuggested([]))
        .finally(() => setSuggLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [form.nome, mode]);

  // Ricerca in modalità associa
  const { data: candidati = [] } = useMagazzinoSearch(mode === "associa" ? search : "");

  const associa = async (materiale_id) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/magazzino/materiali/${materiale_id}/associa-barcode`, { barcode: code });
      toast.success(`Barcode associato a ${data.nome}`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore associazione");
    } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Inserisci il nome dell'articolo");
    setSaving(true);
    try {
      const payload = {
        ...form,
        barcode: code,
        quantita_disponibile: Number(form.quantita_disponibile) || 0,
        prezzo_unitario: Number(form.prezzo_unitario) || 0,
      };
      const { data } = await api.post("/magazzino/materiali", payload);
      toast.success(`Articolo creato: ${data.nome}`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-articolo-non-trovato"
      data-scan-safe
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
          <div>
            <div className="overline text-amber-700">Barcode non trovato</div>
            <h3 className="font-display font-bold text-lg">Cosa vuoi fare?</h3>
            <div className="text-xs text-slate-500 font-mono mt-1">Barcode: {code}</div>
          </div>
        </div>

        {mode === "choose" && (
          <div className="grid grid-cols-1 gap-2 pt-2">
            <button type="button" onClick={() => setMode("associa")} data-testid="btn-associa-esistente"
              className="w-full text-left p-3 border-2 border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-md">
              <div className="font-bold text-emerald-900">📎 Associa a materiale esistente</div>
              <div className="text-xs text-slate-600 mt-0.5">Cerca un articolo già in magazzino e collega questo barcode.</div>
            </button>
            <button type="button" onClick={() => setMode("crea")} data-testid="btn-crea-nuovo"
              className="w-full text-left p-3 border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-md">
              <div className="font-bold text-blue-900">➕ Crea nuovo materiale</div>
              <div className="text-xs text-slate-600 mt-0.5">Registra un articolo nuovo. Barcode assegnato automaticamente.</div>
            </button>
            <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-900 pt-1">Annulla</button>
          </div>
        )}

        {mode === "associa" && (
          <div className="space-y-2">
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, codice, categoria..." data-testid="input-search-associa" data-no-uppercase
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <div className="border border-slate-200 rounded-sm max-h-64 overflow-y-auto">
              {candidati.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400">
                  {search.length < 2 ? "Digita almeno 2 caratteri per cercare" : "Nessun materiale trovato"}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {candidati.map((m) => (
                    <li key={m.materiale_id} className="p-3 flex items-center gap-3 hover:bg-slate-50">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{m.nome}</div>
                        <div className="text-[11px] text-slate-500 font-mono truncate">
                          {m.codice || "—"}{m.barcode ? ` · BC ${m.barcode}` : ""} · {m.categoria}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          associa(m.materiale_id);
                        }}
                        disabled={saving}
                        data-testid={`btn-associa-${m.materiale_id}`}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm disabled:opacity-50">
                        {saving ? "..." : "Associa"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-between">
              <button onClick={() => setMode("choose")} className="text-xs text-slate-500 hover:text-slate-900">← Indietro</button>
              <button onClick={() => setMode("crea")} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">Non lo trovo → crea nuovo</button>
            </div>
          </div>
        )}

        {mode === "crea" && (
          <form onSubmit={submit} className="space-y-2">
            <input required placeholder="Nome articolo *" value={form.nome} autoFocus
              onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="nf-nome"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            {suggested.length > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm">
                <div className="text-[10px] uppercase font-bold text-amber-800 mb-1">⚠️ Simili già in magazzino</div>
                <ul className="space-y-1">
                  {suggested.map((m) => (
                    <li key={m.materiale_id} className="text-xs flex items-center justify-between gap-2">
                      <span className="truncate flex-1">{m.nome}</span>
                      <button type="button" onClick={() => associa(m.materiale_id)}
                        className="px-2 py-0.5 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold rounded-sm">
                        Usa questo
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                data-testid="nf-cat" className="px-3 py-2 border border-slate-300 rounded-sm text-sm">
                <option value="generico">Generico</option>
                <option value="edile">Edile</option>
                <option value="elettrico">Elettrico</option>
                <option value="idrico">Idrico</option>
                <option value="condizionamento">Condizionamento</option>
                <option value="finiture">Finiture</option>
              </select>
              <input placeholder="Unità (pz, m, kg…)" value={form.unita_misura}
                onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} data-testid="nf-um"
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <input type="number" step="0.01" placeholder="Giacenza iniziale" value={form.quantita_disponibile}
                onChange={(e) => setForm({ ...form, quantita_disponibile: e.target.value })} data-testid="nf-qta" data-no-uppercase
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <input type="number" step="0.01" placeholder="Prezzo unit. €" value={form.prezzo_unitario}
                onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} data-testid="nf-prezzo" data-no-uppercase
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setMode("choose")}
                className="flex-1 px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-bold uppercase rounded-sm">
                ← Indietro
              </button>
              <button type="submit" disabled={saving} data-testid="nf-crea"
                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase rounded-sm disabled:opacity-50">
                {saving ? "Salvataggio…" : "Crea articolo"}
              </button>
            </div>
            <div className="text-[11px] text-slate-500 italic border-t border-slate-100 pt-2 flex items-start gap-1">
              <ClipboardList className="w-3 h-3 shrink-0 mt-0.5" />
              Il barcode <b>{code}</b> verrà associato a questo articolo.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function useMagazzinoSearch(q) {
  const [data, setData] = useState([]);
  useEffect(() => {
    if (!q || q.length < 2) { setData([]); return; }
    const t = setTimeout(() => {
      api.get(`/magazzino/materiali/simili?q=${encodeURIComponent(q)}&limit=20`)
        .then((r) => setData(r.data)).catch(() => setData([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return { data };
}
